/**
 * The player's observable state, the handle the engine mutates it through, and
 * the pure decisions that read it.
 *
 * State lives in one per-provider atom. The handle is deliberately narrow —
 * `getState`/`setState` — because the engine runs as fibers that write from
 * arbitrary suspension points, and the registry is the only thing that may own
 * the value.
 */
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import { Atom, type AtomRegistry } from 'effect/reactivity';
import type {
  DocPackReadingAnchor,
  DocPackReadingChunk,
} from '../doc-pack/reading-plan';
import type { NotePlaybackTarget } from './note-playback';
import {
  emptyPlaybackSessionStats,
  type PlaybackSessionStats,
} from './playback-stats';

export type LoadPos =
  | { posMs: number }
  | {
      wordIdxInChunk: number;
      /** Set when the index was read from another recording of the chunk
       * (another voice): the index is trusted only if the loaded chunk has
       * the same number of words, otherwise playback resumes at `posMs`. */
      readFrom?: { wordCount: number; posMs: number };
    }
  | { docPackPage: number; docPackCharStart: number };

export type CommonLoadParams = {
  loadPos: LoadPos;
  autoPlay?: boolean;
  docPackFrom?: DocPackReadingAnchor | undefined;
};

export type AudioChunkPlayerState = {
  audioId?: string | undefined;
  lookupAudioId?: string | undefined;
  chapterId?: string | undefined;
  ttsId?: string | undefined;
  playbackMode: 'legacy' | 'doc-pack';
  docPackChunk?: DocPackReadingChunk | undefined;
  /** What the engine is loading or last loaded the current chunk in — the
   * voice, and for doc-pack the pack version. The UI reads the current chunk
   * under these, never under values it derives itself, so it always looks for
   * the chunk the engine fetched. */
  loadedVoice?: string | undefined;
  loadedDocPackVersion?: number | undefined;

  loadPos: LoadPos;
  autoPlay: boolean;

  isLoadingAudio: boolean;
  isPlaybackComplete: boolean;
  /** Terminal playback error, rendered instead of a loading spinner:
   * 'network' = the chunk-fetch circuit opened (sustained backend failure),
   * 'chunk' = the current chunk gave up after repeated load failures.
   * Cleared when a chunk becomes playable or the user retries successfully. */
  playbackFault: 'network' | 'chunk' | null;
  stats: PlaybackSessionStats;
};

export const initialPlaybackState: AudioChunkPlayerState = {
  audioId: undefined,
  lookupAudioId: undefined,
  chapterId: undefined,
  ttsId: undefined,
  playbackMode: 'legacy',
  docPackChunk: undefined,
  loadedVoice: undefined,
  loadedDocPackVersion: undefined,
  loadPos: { posMs: 0 },
  autoPlay: true,
  isLoadingAudio: false,
  isPlaybackComplete: false,
  playbackFault: null,
  stats: emptyPlaybackSessionStats,
};

export type AudioChunkPlayerStoreHandle = {
  readonly atom: Atom.Writable<AudioChunkPlayerState>;
  readonly getState: () => AudioChunkPlayerState;
  readonly setState: (partial: Partial<AudioChunkPlayerState>) => void;
};

export const createAudioChunkPlayerStoreHandle = ({
  registry,
  initialAudioId,
  initialLookupAudioId,
}: {
  registry: AtomRegistry.AtomRegistry;
  initialAudioId?: string | undefined;
  initialLookupAudioId?: string | undefined;
}): AudioChunkPlayerStoreHandle => {
  // Deliberately NOT keepAlive: the atom is per-provider-mount, and the
  // provider itself subscribes for its whole lifetime, so the node stays alive
  // while mounted and the registry reclaims it after unmount — keepAlive here
  // would leak a full player state per route navigation on apps/start
  // (session-long registry).
  const atom = Atom.make<AudioChunkPlayerState>({
    ...initialPlaybackState,
    audioId: initialAudioId,
    lookupAudioId: initialLookupAudioId,
  });
  return {
    atom,
    getState: () => registry.get(atom),
    setState: (partial) => {
      registry.update(atom, (state) => ({ ...state, ...partial }));
    },
  };
};

/** The chunk a command is operating on. Comparing a captured target against
 * the live one is how work that outlived its load — a detached prefetch, a
 * boundary advance resolved across an await — notices the user has moved on. */
export type PlaybackTarget =
  | {
      readonly playbackMode: 'doc-pack';
      readonly audioId: string;
      readonly docPackChunkId: string;
    }
  | {
      readonly playbackMode: 'legacy';
      readonly audioId: string;
      readonly chapterId: string;
      readonly ttsId: string;
    };

/** `undefined` when the state names no complete chunk, so a half-populated
 * store matches nothing. */
export function playbackTargetOf({
  audioId,
  chapterId,
  ttsId,
  playbackMode,
  docPackChunk,
}: Pick<
  AudioChunkPlayerState,
  'audioId' | 'chapterId' | 'ttsId' | 'playbackMode' | 'docPackChunk'
>): PlaybackTarget | undefined {
  if (!audioId) return undefined;
  if (playbackMode === 'doc-pack') {
    const docPackChunkId = docPackChunk?.id;
    return docPackChunkId === undefined
      ? undefined
      : { playbackMode, audioId, docPackChunkId };
  }
  return chapterId && ttsId
    ? { playbackMode, audioId, chapterId, ttsId }
    : undefined;
}

export function isSamePlaybackTarget(
  a: PlaybackTarget | undefined,
  b: PlaybackTarget | undefined,
) {
  if (!(a && b) || a.audioId !== b.audioId) return false;
  if (a.playbackMode === 'doc-pack')
    return (
      b.playbackMode === 'doc-pack' && a.docPackChunkId === b.docPackChunkId
    );
  return (
    b.playbackMode === 'legacy' &&
    a.chapterId === b.chapterId &&
    a.ttsId === b.ttsId
  );
}

/** What a same-item load resolves to — exactly what the caller can observe:
 * do nothing, resume the loaded queue, or run the full fresh-load path (which
 * covers replay, a faulted item, and a missing or half-loaded session
 * alike). */
export type SameItemPlaybackAction =
  | 'no-op'
  | 'play-existing-queue'
  | 'load-fresh';

export function resolveSameItemPlaybackAction({
  shouldPlay,
  forcePlay,
  isPlaying,
  hasAudioSource,
  hasLoadedAudio,
  hasLoadedChunk,
  hasPlaybackFault,
}: {
  shouldPlay: boolean;
  forcePlay: boolean;
  isPlaying: boolean;
  hasAudioSource: boolean;
  hasLoadedAudio: boolean;
  hasLoadedChunk: boolean;
  hasPlaybackFault: boolean;
}): SameItemPlaybackAction {
  // A faulted item has no playable queue to resume; only a fresh load
  // (which re-requests the failed chunk) recovers. Ahead of every other
  // branch because `beginPlaybackCommand` has already cleared the fault by
  // the time this runs: any answer but `load-fresh` drops the incident on the
  // floor, leaving the caller pending with the retry affordance gone.
  if (hasPlaybackFault) return 'load-fresh';

  if (!shouldPlay) return hasLoadedChunk ? 'no-op' : 'load-fresh';
  if (!hasLoadedAudio) return 'load-fresh';
  if (!forcePlay && isPlaying) return 'no-op';
  if (!forcePlay && hasAudioSource) return 'play-existing-queue';
  return 'load-fresh';
}

export function audioProgressLoadArgs(
  progress: AudioItemSummary['progress'] | undefined | null,
): {
  chapterId: string | undefined;
  ttsId: string | undefined;
  loadParams: CommonLoadParams;
} {
  const docPackFrom =
    progress?.doc_pack_page !== null &&
    progress?.doc_pack_page !== undefined &&
    progress.doc_pack_char_start !== null &&
    progress.doc_pack_char_start !== undefined
      ? {
          page: progress.doc_pack_page,
          charStart: progress.doc_pack_char_start,
        }
      : undefined;

  return {
    chapterId: progress?.chapter_id ?? undefined,
    ttsId: progress?.tts_id ?? undefined,
    loadParams: {
      loadPos: progress?.tts_id
        ? { posMs: progress.timestamp_ms }
        : docPackFrom
          ? {
              docPackPage: docPackFrom.page,
              docPackCharStart: docPackFrom.charStart,
            }
          : { posMs: 0 },
      docPackFrom,
    },
  };
}

/** Dispatch, not invoke: every one of these records intent and returns.
 *
 * Nothing here is awaitable by design. A playback command is latest-wins and
 * interruptible, so a per-invocation promise could only ever lie — it would
 * have to resolve when the command was superseded, which is not "done". Read
 * progress and failure from player state (`isLoadingAudio`, `playbackFault`)
 * or, for the settled outcome of the most recent command, through
 * `useAfterPlaybackCommand`. */
export type AudioChunkPlayerActions = {
  pause: () => void;
  play: () => void;
  togglePlayPause: () => void;
  loadAudio: (
    audioId: string,
    chapterId?: string,
    ttsId?: string,
    loadParams?: CommonLoadParams,
  ) => void;
  unloadAudio: () => void;
  skipToChunk: (ttsId: string, loadParams?: CommonLoadParams) => void;
  skipToDocPackRange: (
    anchor: DocPackReadingAnchor,
    loadParams?: CommonLoadParams,
  ) => void;
  reloadChunk: () => void;
  skipToChapterStart: (
    chapterId: string,
    loadParams?: CommonLoadParams,
  ) => void;
  loadAudioFromProgress: (audio: AudioItemSummary) => void;
  loadAudioAtNote: (audioId: string, noteTarget: NotePlaybackTarget) => void;
  skipToNote: (noteTarget: NotePlaybackTarget) => void;
};
